import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Mail, CheckCircle, AlertCircle, Send, Loader2, Edit } from 'lucide-react';
import AccountLayout from '../components/AccountLayout';

export default function EmailVerificationPage() {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [changeEmailMode, setChangeEmailMode] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleResendVerification = async () => {
    setResendLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // TODO: Implement actual API call
      // await clientAuthApi.resendVerificationEmail();

      setSuccessMessage(t('auth.account.emailVerification.resendSuccess'));
      
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Failed to resend verification email:', error);
      setErrorMessage(t('auth.account.emailVerification.resendError'));
    } finally {
      setResendLoading(false);
    }
  };

  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newEmail)) {
        setErrorMessage(t('auth.account.emailVerification.invalidEmail'));
        setLoading(false);
        return;
      }

      if (!password) {
        setErrorMessage(t('auth.account.emailVerification.passwordRequired'));
        setLoading(false);
        return;
      }

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // TODO: Implement actual API call
      // await clientAuthApi.changeEmail({ newEmail, password });

      setSuccessMessage(t('auth.account.emailVerification.changeSuccess'));
      setChangeEmailMode(false);
      setNewEmail('');
      setPassword('');
      
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Failed to change email:', error);
      setErrorMessage(t('auth.account.emailVerification.changeError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AccountLayout>
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-display font-bold text-textPrimary mb-2">
            {t('auth.account.emailVerification.title')}
          </h1>
          <p className="text-textSecondary">
            {t('auth.account.emailVerification.subtitle')}
          </p>
        </div>

        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        )}

        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800">{errorMessage}</p>
          </div>
        )}

        {/* Current Email Status */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
              <Mail className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-textPrimary">
                {t('auth.account.emailVerification.currentEmail')}
              </h2>
              <p className="text-sm text-textSecondary">
                {t('auth.account.emailVerification.currentEmailDesc')}
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="font-medium text-textPrimary">{user?.email}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    {user?.emailVerified ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-600 font-medium">
                          {t('auth.account.emailVerification.verified')}
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4 text-orange-600" />
                        <span className="text-sm text-orange-600 font-medium">
                          {t('auth.account.emailVerification.unverified')}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setChangeEmailMode(!changeEmailMode)}
                className="btn-secondary flex items-center space-x-2"
              >
                <Edit className="w-4 h-4" />
                <span>{t('auth.account.emailVerification.changeEmail')}</span>
              </button>
            </div>
          </div>

          {!user?.emailVerified && !changeEmailMode && (
            <div>
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg mb-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-orange-900 mb-1">
                      {t('auth.account.emailVerification.notVerifiedTitle')}
                    </p>
                    <p className="text-orange-700">
                      {t('auth.account.emailVerification.notVerifiedDesc')}
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleResendVerification}
                disabled={resendLoading}
                className="btn-primary flex items-center space-x-2"
              >
                {resendLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{t('auth.account.emailVerification.sending')}</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>{t('auth.account.emailVerification.resendButton')}</span>
                  </>
                )}
              </button>
            </div>
          )}

          {user?.emailVerified && !changeEmailMode && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-green-900 mb-1">
                    {t('auth.account.emailVerification.verifiedTitle')}
                  </p>
                  <p className="text-green-700">
                    {t('auth.account.emailVerification.verifiedDesc')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Change Email Form */}
        {changeEmailMode && (
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-xl font-semibold text-textPrimary mb-4">
              {t('auth.account.emailVerification.changeEmailTitle')}
            </h2>
            <p className="text-sm text-textSecondary mb-6">
              {t('auth.account.emailVerification.changeEmailDesc')}
            </p>

            <form onSubmit={handleChangeEmail} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  {t('auth.account.emailVerification.newEmail')}
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder={t('auth.account.emailVerification.newEmailPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  {t('auth.account.emailVerification.confirmPassword')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.account.emailVerification.confirmPasswordPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium mb-1">
                      {t('auth.account.emailVerification.changeWarningTitle')}
                    </p>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>{t('auth.account.emailVerification.changeWarning1')}</li>
                      <li>{t('auth.account.emailVerification.changeWarning2')}</li>
                      <li>{t('auth.account.emailVerification.changeWarning3')}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary flex items-center space-x-2 flex-1"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>{t('auth.account.emailVerification.updating')}</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      <span>{t('auth.account.emailVerification.updateEmail')}</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setChangeEmailMode(false);
                    setNewEmail('');
                    setPassword('');
                    setErrorMessage('');
                  }}
                  className="btn-secondary"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Info Section */}
        {!changeEmailMode && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
            <h3 className="font-semibold text-textPrimary mb-3">
              {t('auth.account.emailVerification.whyVerify.title')}
            </h3>
            <ul className="space-y-2 text-sm text-textSecondary">
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <span>{t('auth.account.emailVerification.whyVerify.reason1')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <span>{t('auth.account.emailVerification.whyVerify.reason2')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <span>{t('auth.account.emailVerification.whyVerify.reason3')}</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                <span>{t('auth.account.emailVerification.whyVerify.reason4')}</span>
              </li>
            </ul>
          </div>
        )}
      </div>
    </AccountLayout>
  );
}

